<script>
  /** @type {'large'|'small'|null} */
  export let size = null
  export let center = false
  export let muted = false
</script>

<div
  class:center
  class:muted
  class:large={size === 'large'}
  class:small={size === 'small'}
  class="html {$$restProps.class || ''}">
  <slot />
</div>

<style>
  .html {
    width: 100%;
    max-width: var(--current-max-width, 36em);
    color: var(--current-color, rgb(var(--rgb-text)));
    font-size: var(--current-font-size, 1.125rem);
    text-align: var(--current-text-align, left);
    line-height: var(--current-line-height, 1.45);
  }

  .large {
    font-size: var(--current-font-size, 1.25rem);
    max-width: var(--current-max-width, 34em);
  }

  .small {
    font-size: var(--current-font-size, 1rem);
  }

  .muted {
    --current-color: rgb(var(--rgb-muted));
  }

  .html :global(.muted) {
    --current-color: rgb(var(--rgb-muted));
    color: var(--current-color);
  }

  .center {
    text-align: center;
  }

  @media (min-width: 1000px) {
    .html {
      font-size: var(--current-font-size, 1.25rem);
    }

    .small {
      font-size: var(--current-font-size, 1.125rem);
    }

    .large {
      font-size: var(--current-font-size, 1.5rem);
    }
  }

  .middle {
    margin-left: auto;
    margin-right: auto;
  }

  .html :global(strong) {
    font-weight: 600;
  }

  /**
   * Links
   */

  .html :global(:is(a, .link)) {
    color: rgb(var(--rgb-link));
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.15em;
    text-decoration-color: rgba(var(--rgb-link), 0.25);
    font-weight: 500;
    word-break: break-word;
    hyphens: auto;
    cursor: pointer;
    transition:
      var(--color-transition),
      text-decoration-color 100ms;
  }

  .html :global(:is(a.muted, .link.muted)) {
    color: rgb(var(--rgb-muted));
    text-decoration: none;
  }

  .html.large :global(:is(a, .link)) {
    font-weight: 400;
  }

  .html :global(:is(a:hover, .link:hover)) {
    text-decoration-color: rgba(var(--rgb-link), 1);
  }

  .html :global(:is(a:active, .link:active)) {
    opacity: 0.7;
    transition: var(--color-active-transition);
  }

  .html :global(a:has(> :is(h1, h2, h3, h4))) {
    text-decoration: none !important;
  }

  /**
   * Block elements
   */

  .html :global(img) {
    margin: 1.5em 0;
    border-radius: var(--border-radius-lg);
  }

  .html :global(img:first-child),
  .html :global(p:first-child:empty + img) {
    margin-top: 0;
  }

  .html :global(:is(p, ul, ol, h1, h2, h3, h4)) {
    margin: 0.6em 0;
  }

  .small :global(:is(p, ul, ol, h1, h2, h3, h4)) {
    margin: 0.5em 0;
  }

  .html :global(:is(p, ul, ol, h1, h2, h3, h4):first-child) {
    margin-top: 0;
  }

  .html :global(:is(p, ul, ol, h1, h2, h3, h4):last-child) {
    margin-bottom: 0;
  }

  .html :global(pre) {
    width: 100%;
    padding: 0.6rem 0.5rem;
    margin: 2.2rem 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    font-size: 14px;
    text-align: left;
    line-height: 1.2;
    color: #fff;
    border-radius: 0.5rem;
    background: #000;
  }

  .html :global(p) {
    max-width: var(--current-p-max-width, 100%);
  }

  .html :global(p:empty) {
    display: none;
  }

  /**
   * Lists
   */

  .html :global(:is(ul, ol)) {
    margin: 0;
  }

  .html :global(:is(ul, ol, dl):not(:first-child)) {
    margin-top: 0.7em;
  }

  .html :global(:is(ul, ol):not(:last-child)) {
    margin: 1em 0 1em 0.8em;
  }

  .html :global(ul) {
    list-style-type: none;
  }

  .html :global(ol) {
    list-style-type: decimal;
    padding-left: 1.7em;
  }

  .html :global(li + li) {
    margin-top: 0.6em;
  }

  .html :global(ul > li) {
    padding-left: 1em;
    position: relative;
  }

  .html :global(ol > li) {
    padding-left: 0.5em;
  }

  .html :global(ul > li::before) {
    content: '';
    width: 0.35em;
    height: 0.35em;
    border-radius: 50%;
    position: absolute;
    left: 0;
    top: 0.55em;
    background: rgb(var(--rgb-muted));
  }

  .html :global(dl) {
    display: grid;
    gap: 0.15rem;
    grid-template-columns: 1fr auto;
    align-items: baseline;
  }

  .html :global(dt) {
    grid-column: 1 / 1;
    display: flex;
    align-items: center;
    font-weight: 600;
  }

  .html :global(dt::after) {
    content: '';
    flex: 1 1 auto;
    height: 3px;
    margin: 0 0.5rem;
    background-image: radial-gradient(
      circle,
      rgb(var(--rgb-muted), 0.4) 1px,
      transparent 1px
    );
    background-size: 6px 6px; /* 2px for the circle diameter + 4px for the spacing */
    background-position:
      0 50%,
      3px 50%; /* Position the dots in the middle of the line */
    background-repeat: repeat-x;
  }

  .html :global(dd) {
    grid-column: 2 / 2;
  }

  .html :global(.plain) {
    list-style: none;
  }

  .html :global(:is(.plain > li, li.plain)) {
    padding-left: 0;
  }

  .html :global(:is(.plain > li::before, li.plain::before)) {
    content: none;
  }

  /**
   * Headings
   */

  .html :global(:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5)) {
    font-weight: bold;
    line-height: 1.25;
    font-family: var(--heading-font-family);
    color: var(--current-heading-color, rgb(var(--rgb-text)));
    word-spacing: -0.05em;
    letter-spacing: -0.025em;
    text-wrap: balance;
    width: 85%;
  }

  .html.center :global(:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5)) {
    margin: 0 auto;
  }

  .html :global(:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5) em),
  .html :global(:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5) em strong) {
    font-weight: normal;
    font-style: normal;
  }

  .html :global(:is(h1, .h1)) {
    font-size: var(--current-h1-font-size, 3.5rem);
    line-height: var(--current-h1-line-height, 0.95);
    margin: var(--current-h1-margin, 0.75em 0 0.3em);
  }

  .html :global(:is(h2, .h2)) {
    font-size: var(--current-h2-font-size, 2.25rem);
    line-height: var(--current-h2-line-height, 1.05);
    margin: var(--current-h2-margin, 1.25em 0 0rem);
  }

  .html :global(:is(h3, .h3)) {
    font-size: var(--current-h3-font-size, 1.5rem);
    line-height: var(--current-h3-line-height, 1.15);
    margin: var(--current-h3-margin, 1.5em 0 0);
  }

  .html :global(:is(h4, .h4)) {
    font-size: var(--current-h3-font-size, 1.25rem);
    line-height: var(--current-h3-line-height, 1.2);
    margin: var(--current-h3-margin, 1.5em 0 0);
  }

  .html :global(:is(h5, .h5)) {
    font-size: var(--current-h5-font-size, 1em);
    line-height: var(--current-h5-line-height, inherit);
    font-weight: bold;
    margin: 2.25em 0 -0.5em;
  }

  /* Special margins that works good in list formats, such as FAQs */

  .html :global(.legend) {
    margin: 1.3em 0 0.5em !important;
  }

  .html :global(.legend a) {
    color: inherit;
  }

  @media (min-width: 900px) {
    .html :global(:is(h1, .h1)) {
      font-size: var(--current-h1-font-size, 5rem);
      line-height: var(--current-h1-line-height, 0.9);
      margin: 0.625em 0 0.3em;
      margin: var(--current-h2-margin, 0.625em 0 0.3em);
    }

    .html :global(:is(h2, .h2)) {
      font-size: var(--current-h2-font-size, 3.5rem);
      line-height: var(--current-h2-line-height, 0.95);
    }

    .html :global(:is(h3, .h3)) {
      font-size: var(--current-h3-font-size, 1.6rem);
      line-height: var(--current-h3-line-height, 1.1);
    }

    .html :global(:is(h4, .h4)) {
      font-size: var(--current-h3-font-size, 1.4rem);
      line-height: var(--current-h3-line-height, 1.1);
    }
  }
</style>
